import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon } from './Icons.jsx';
import { api } from '../services/api/client.js';
import { track } from '../lib/funnel.js';
import { useUpgrade } from '../contexts/UpgradeContext.jsx';
import { usePlanCatalog } from '../hooks/usePlanCatalog.js';
import { NEW_PLAN_DEFS } from '../lib/plans.js';
import {
  buildPaymentIssueCopy,
  dismissPaymentIssue,
  isLapsedPaymentIssue,
  paymentIssueDismissKey,
  paymentIssueLinks,
  shouldHidePaymentIssue,
} from '../lib/paymentIssue.js';

// Backend planKey ('growth', 'brand_pilot', ...) -> a human display name, via
// the same DB-backed catalog + newGridActive disambiguation UpgradeModal and
// Settings already use. Falls back to a title-cased raw key so an unresolved
// (e.g. fully retired) plan still reads as a name, never a blank.
function resolvePlanName(planKey, { getPlanDisplay, getNewPlanDisplay, newGridActive }) {
  if (!planKey) return null;
  const display = (newGridActive && NEW_PLAN_DEFS.some((p) => p.id === planKey))
    ? getNewPlanDisplay(planKey)
    : getPlanDisplay(planKey);
  return display?.name || String(planKey).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function PaymentIssueNotice({ paymentIssue, planName, graceEndsAt, compact = false, dismissible = true }) {
  const { t, i18n } = useTranslation();
  const { openUpgrade } = useUpgrade();
  const { getPlanDisplay, getNewPlanDisplay, newGridActive } = usePlanCatalog();
  const dismissKey = paymentIssueDismissKey(paymentIssue);
  const [dismissed, setDismissed] = useState(() => dismissible && shouldHidePaymentIssue(paymentIssue));
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const links = paymentIssueLinks(paymentIssue, typeof window !== 'undefined' ? window.location.href : null);
  // TASK-331: readPaymentIssue now also covers a lapsed org (canceled, or a
  // dunning status past the grace window). The dunning rendering below is
  // untouched byte-for-byte; this only decides which copy/action row to show.
  const lapsed = isLapsedPaymentIssue(paymentIssue);
  // Used only for the Renew button label below (buildPaymentIssueCopy's lapsed
  // branch has no plan-name variant, see the comment there). A lapsed org's
  // CURRENT effective plan is the 0-limit 'lapsed' pseudo-plan, so the
  // caller's `planName` prop (derived from the account's current plan) would
  // misreport "Free" here — the lapsed-widened paymentIssue carries the real
  // paid plan directly; prefer that, falling back to the caller's prop only
  // when it isn't present (older backend).
  const previousPlanName = lapsed
    ? resolvePlanName(paymentIssue?.previousPlan, { getPlanDisplay, getNewPlanDisplay, newGridActive }) || planName || null
    : null;
  const copy = useMemo(
    () => buildPaymentIssueCopy(paymentIssue, planName, t, i18n.language),
    [paymentIssue, planName, t, i18n.language],
  );

  useEffect(() => {
    setDismissed(dismissible && shouldHidePaymentIssue(paymentIssue));
  }, [dismissKey, dismissible, paymentIssue]);

  useEffect(() => {
    if (!paymentIssue || dismissed) return;
    track('payment_issue_banner_shown', { invoiceId: paymentIssue.invoiceId || null, status: paymentIssue.status });
  }, [dismissed, dismissKey, paymentIssue]);

  if (!paymentIssue || dismissed) return null;

  const handlePayNow = () => {
    track('payment_issue_pay_now', { invoiceId: paymentIssue.invoiceId || null });
    window.open(links.payNow, '_blank', 'noopener,noreferrer');
  };

  const handleUpdateCard = async () => {
    if (portalBusy) return;
    track('payment_issue_update_card', { invoiceId: paymentIssue.invoiceId || null });
    setPortalBusy(true);
    setPortalError(null);
    try {
      const returnUrl = new URL('/app/settings?tab=billing', window.location.href).toString();
      const result = await api.billing.createPortalSession({ returnUrl, flow: 'payment_method_update' });
      const portalUrl = result?.checkoutUrl || result?.data?.checkoutUrl || result?.portalUrl || result?.data?.portalUrl;
      if (!portalUrl) throw new Error('missing_portal_url');
      window.location.assign(portalUrl);
    } catch (_) {
      setPortalError(t('paymentIssue.portalError'));
      setPortalBusy(false);
    }
  };

  const handleDismiss = () => {
    dismissPaymentIssue(paymentIssue);
    setDismissed(true);
  };

  // TASK-331: primary action for a lapsed org — opens the shared UpgradeModal
  // in its 'renew' variant (R2 portal + R3/R4 checkout-without-trial; one
  // component, no fork) instead of the dunning "Pay now" framing, which
  // implies a still-live subscription.
  const handleRenew = () => {
    track('payment_issue_renew_clicked', { invoiceId: paymentIssue.invoiceId || null });
    openUpgrade({ feature: 'default', variant: 'renew' });
  };

  return (
    <section className={`card flb-banner flb-payment-issue${compact ? ' flb-compact' : ''}`} role="alert">
      <span className="flb-ic"><Icon name="alert" size={compact ? 13 : 14} /></span>
      <div className="flb-msg">
        <strong>{copy.title}</strong>
        <div>{copy.body}</div>
        {graceEndsAt && <div>{t('grace.accessUntil')} · {new Date(graceEndsAt).toLocaleDateString(i18n.language, { dateStyle: 'medium' })}</div>}
        {portalError && <div className="flb-error">{portalError}</div>}
      </div>
      <div className="flb-actions">
        {lapsed && (
          <button type="button" className="btn btn-primary btn-sm flb-cta" onClick={handleRenew}>
            {previousPlanName ? t('lapsed.renewPlan', { plan: previousPlanName }) : t('lapsed.renew')}
          </button>
        )}
        {links.payNow && (
          <button type="button" className="btn btn-primary btn-sm flb-cta" onClick={handlePayNow}>
            {t('paymentIssue.payNow')}
          </button>
        )}
        <button type="button" className="btn btn-ghost btn-sm flb-cta" onClick={handleUpdateCard} disabled={portalBusy}>
          {portalBusy ? t('paymentIssue.openingPortal') : t('paymentIssue.updateCard')}
        </button>
        <Link className="flb-billing-link" to={links.billingSettings}>{t('paymentIssue.billingSettings')}</Link>
        {dismissible && (
          <button type="button" className="flb-x" aria-label={t('paymentIssue.dismiss')} onClick={handleDismiss}>
            <Icon name="x" size={13} />
          </button>
        )}
      </div>
    </section>
  );
}
